import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// The cursor is drawn as an element that follows the mouse rather than a CSS
// cursor image: browsers drop custom cursor images whenever they would extend
// past the viewport edge (e.g. over the footer) and show the system cursor
// while a not-yet-loaded cursor image is fetched (e.g. on first click).
// hotX/hotY is the pixel of each 72px image that sits on the mouse position.
const SIZE = 72;
const CURSORS = {
  arrow: { src: "/arrow_dark.svg", hotX: 16, hotY: 8 },
  pointer: { src: "/pointer_dark.svg", hotX: 24, hotY: 4 },
  active: { src: "/pointer_active_dark.svg", hotX: 24, hotY: 4 },
};

const POINTER_TARGETS =
  "a, button, input, select, textarea, label, .hover, span, li, blockquote, code, u, .calendar-cell";
const PRESS_TARGETS = "a, button, input, select, textarea, label, .active";
const FINE_POINTER = "(hover: hover) and (pointer: fine)";
// ScrollSmoother keeps moving the page for ~2s after a scroll ends
const SCROLL_SETTLE_MS = 2500;

// Over a scrollbar the browser always shows its own cursor, so ours is hidden there
function isOverScrollbar(e) {
  const root = document.documentElement;
  if (e.clientX >= root.clientWidth || e.clientY >= root.clientHeight) {
    return true;
  }
  const t = e.target;
  if (!(t instanceof HTMLElement) || t === root || t === document.body) {
    return false;
  }
  if (t.scrollWidth <= t.clientWidth && t.scrollHeight <= t.clientHeight) {
    return false;
  }
  const cs = getComputedStyle(t);
  const borderX =
    parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
  const borderY =
    parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
  const verticalBar = t.offsetWidth - t.clientWidth - borderX;
  const horizontalBar = t.offsetHeight - t.clientHeight - borderY;
  return (
    (verticalBar > 0 && e.offsetX > t.clientWidth) ||
    (horizontalBar > 0 && e.offsetY > t.clientHeight)
  );
}

export default function Cursor() {
  const [enabled] = useState(() => window.matchMedia(FINE_POINTER).matches);
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    let pressed = false;
    let last = null;
    let settleUntil = 0;
    let frame = 0;

    // Decode every image up front so switching never shows a blank frame
    Object.values(CURSORS).forEach(({ src }) => {
      const img = new Image();
      img.src = src;
      img.decode?.().catch(() => {});
    });

    const typeFor = (target) => {
      if (!(target instanceof Element)) return "arrow";
      if (pressed && target.closest(PRESS_TARGETS)) return "active";
      return target.closest(POINTER_TARGETS) ? "pointer" : "arrow";
    };
    const setType = (target) => {
      const type = typeFor(target);
      if (el.dataset.type !== type) el.dataset.type = type;
    };

    const onMove = (e) => {
      last = { x: e.clientX, y: e.clientY };
      el.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      el.style.opacity = isOverScrollbar(e) ? "0" : "1";
      setType(e.target);
    };
    const onDown = (e) => {
      pressed = true;
      setType(e.target);
    };
    const onUp = (e) => {
      pressed = false;
      setType(e.target);
    };
    const onLeaveWindow = (e) => {
      if (!e.relatedTarget) el.style.opacity = "0";
    };

    // Content moves under a still mouse while scrolling, so re-check what is
    // under the cursor until the smooth scroll settles
    const recheck = () => {
      if (last) setType(document.elementFromPoint(last.x, last.y));
      frame =
        performance.now() < settleUntil ? requestAnimationFrame(recheck) : 0;
    };
    const onScroll = () => {
      settleUntil = performance.now() + SCROLL_SETTLE_MS;
      if (!frame) frame = requestAnimationFrame(recheck);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    document.addEventListener("mouseout", onLeaveWindow);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("mouseout", onLeaveWindow);
      cancelAnimationFrame(frame);
    };
  }, [enabled]);

  if (!enabled) return null;

  return createPortal(
    <>
      <style>
        {`
          html, body, * {
            cursor: none !important;
          }
          .custom-cursor img {
            display: none;
          }
          .custom-cursor[data-type="arrow"] img[data-cursor="arrow"],
          .custom-cursor[data-type="pointer"] img[data-cursor="pointer"],
          .custom-cursor[data-type="active"] img[data-cursor="active"] {
            display: block;
          }
        `}
      </style>
      <div
        ref={ref}
        className="custom-cursor"
        data-type="arrow"
        aria-hidden="true"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          zIndex: 2147483647,
          pointerEvents: "none",
          opacity: 0,
          willChange: "transform",
        }}
      >
        {Object.entries(CURSORS).map(([type, { src, hotX, hotY }]) => (
          <img
            key={type}
            src={src}
            alt=""
            data-cursor={type}
            draggable={false}
            width={SIZE}
            height={SIZE}
            style={{
              position: "absolute",
              left: -hotX,
              top: -hotY,
              width: SIZE,
              height: SIZE,
              maxWidth: "none",
            }}
          />
        ))}
      </div>
    </>,
    document.body,
  );
}
